import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { ADMIN_NAV } from "@/pages/admin/AdminHome";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FinePrint } from "@/components/FinePrint";
import { Loader2, Search, MailCheck, MailX, CheckCircle2, XCircle, HelpCircle, UserPlus, ShieldAlert, RefreshCw } from "lucide-react";

const STATUS_META = {
  pending_email: { label: "Pending Email", cls: "bg-slate-100 text-slate-600" },
  pending_approval: { label: "Pending Approval", cls: "bg-amber-100 text-amber-800" },
  more_info: { label: "More Info Requested", cls: "bg-sky-100 text-sky-800" },
  approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-700" },
  cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-400" },
};
const FILTERS = [
  { v: "pending_approval", label: "Pending Approval" },
  { v: "more_info", label: "More Info" },
  { v: "pending_email", label: "Pending Email" },
  { v: "approved", label: "Approved" },
  { v: "rejected", label: "Rejected" },
  { v: "all", label: "All" },
];

function Badge({ status }) {
  const m = STATUS_META[status] || STATUS_META.cancelled;
  return <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${m.cls}`}>{m.label}</span>;
}

export function AdminRegistrations() {
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("pending_approval");
  const [q, setQ] = useState("");
  const [admins, setAdmins] = useState([]);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [approveRole, setApproveRole] = useState("student");
  const [approveStatus, setApproveStatus] = useState("active");
  const [rejectInternal, setRejectInternal] = useState("");
  const [rejectMsg, setRejectMsg] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [note, setNote] = useState("");

  const load = () => {
    const params = { status: filter };
    if (q) params.q = q;
    api.get("/admin/registration/requests", { params }).then(({ data }) => setRows(data)).catch(() => setRows([]));
    api.get("/admin/registration/summary").then(({ data }) => setSummary(data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);
  useEffect(() => { api.get("/admin/registration/admins").then(({ data }) => setAdmins(data)).catch(() => {}); }, []);

  const openDetail = async (id) => {
    const { data } = await api.get(`/admin/registration/requests/${id}`);
    setDetail(data);
    setApproveRole(data.requested_role === "faculty" ? "faculty"
      : data.requested_role === "advisor" ? "advisor"
      : data.requested_role === "employer" ? "employer" : "student");
    setApproveStatus("active");
    setRejectInternal(""); setRejectMsg(""); setInfoMsg(""); setNote("");
  };

  const refreshDetail = async (id) => { await openDetail(id); load(); };

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Action failed"); }
    finally { setBusy(false); }
  };

  const approve = () => act(async () => {
    const { data } = await api.post(`/admin/registration/requests/${detail.id}/approve`, { approved_role: approveRole, account_status: approveStatus });
    toast.success(data.emailed ? "Approved — welcome email sent" : (data.account_status === "inactive" ? "Approved — account created (inactive)" : "Approved — account activated (email not delivered)"));
    await refreshDetail(detail.id);
  });
  const reject = () => act(async () => {
    await api.post(`/admin/registration/requests/${detail.id}/reject`, { internal_reason: rejectInternal, applicant_message: rejectMsg || undefined });
    toast.success("Request rejected"); await refreshDetail(detail.id);
  });
  const requestInfo = () => act(async () => {
    await api.post(`/admin/registration/requests/${detail.id}/request-info`, { message: infoMsg });
    toast.success("Information request sent"); await refreshDetail(detail.id);
  });
  const assign = (assignee_id) => act(async () => {
    await api.post(`/admin/registration/requests/${detail.id}/assign`, { assignee_id: assignee_id || null });
    toast.success("Assignment updated"); await refreshDetail(detail.id);
  });
  const addNote = () => act(async () => {
    await api.post(`/admin/registration/requests/${detail.id}/note`, { text: note });
    setNote(""); toast.success("Note added"); await refreshDetail(detail.id);
  });
  const resend = () => act(async () => {
    const { data } = await api.post(`/admin/registration/requests/${detail.id}/resend-notification`);
    toast[data.notify_status === "sent" ? "success" : "error"](data.notify_status === "sent" ? "Notification re-sent" : "Delivery failed again");
    await refreshDetail(detail.id);
  });

  const decided = detail && ["approved", "rejected", "cancelled"].includes(detail.status);

  return (
    <DashboardShell title="Registration Requests" subtitle="Review, verify & approve new GPA+ access requests" nav={ADMIN_NAV}>
      {!rows && <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#003057]" /></div>}
      {rows && (
        <div className="space-y-5">
          {summary && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[["Pending approval", summary.pending_approval, "text-amber-600"], ["More info", summary.more_info, "text-sky-600"],
                ["Pending email", summary.pending_email, "text-slate-500"], ["Unassigned tasks", summary.unassigned_tasks, "text-[#003057]"]].map(([l, v, c]) => (
                <Card key={l} className="p-4 card-elevate">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{l}</p>
                  <p className={`font-mono-num text-2xl font-bold ${c}`}>{v}</p>
                </Card>
              ))}
            </div>
          )}

          <Card className="p-4 card-elevate">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                  placeholder="Search by name or email" className="pl-9" data-testid="reg-search" />
              </div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-52" data-testid="reg-filter"><SelectValue /></SelectTrigger>
                <SelectContent>{FILTERS.map((f) => <SelectItem key={f.v} value={f.v}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={load} variant="outline" data-testid="reg-search-btn">Search</Button>
            </div>
          </Card>

          <Card className="p-2 card-elevate sm:p-4">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Requested</TableHead>
                <TableHead>Organization</TableHead><TableHead>Submitted</TableHead><TableHead>Verified</TableHead>
                <TableHead>Status</TableHead><TableHead>Assigned</TableHead><TableHead className="text-right">Action</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`reg-row-${r.id}`}>
                    <TableCell className="font-semibold text-[#003057]">{r.name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{r.email}</TableCell>
                    <TableCell className="text-sm capitalize">{r.requested_role}</TableCell>
                    <TableCell className="text-sm text-slate-500">{r.institution || "—"}</TableCell>
                    <TableCell className="font-mono-num text-xs text-slate-500">{r.created_at?.slice(0, 10)}</TableCell>
                    <TableCell>{r.email_verified
                      ? <MailCheck className="h-4 w-4 text-[#5fa114]" title="Verified" />
                      : <MailX className="h-4 w-4 text-slate-300" title="Not verified" />}</TableCell>
                    <TableCell><Badge status={r.status} /></TableCell>
                    <TableCell className="text-xs text-slate-500">{r.assigned_to_name || "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => openDetail(r.id)} data-testid={`reg-review-${r.id}`}>Review</Button>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={9} className="py-8 text-center text-slate-400" data-testid="reg-empty">No requests match this filter.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>

          <FinePrint>
            New external users cannot access GPA+ until an administrator approves them. Email verification proves
            control of the address but does not by itself grant access. Requested roles are server-enforced —
            applicants can never self-assign privileged roles.
          </FinePrint>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="reg-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-heading text-xl text-[#003057]">
                  <UserPlus className="h-5 w-5" /> {detail.name} <Badge status={detail.status} />
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-5">
                <section className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-xl border border-slate-200 p-4 text-sm">
                  <Field label="Email" value={detail.email} />
                  <Field label="Requested type" value={detail.requested_role} cap />
                  <Field label="Institution" value={detail.institution || "—"} />
                  <Field label="Student/Employee ID" value={detail.id_number || "—"} />
                  {detail.job_title && <Field label="Job title" value={detail.job_title} />}
                  {detail.department && <Field label="Department" value={detail.department} />}
                  {detail.campus && <Field label="Campus" value={detail.campus} />}
                  {detail.program && <Field label="Program / Major" value={detail.program} />}
                  {detail.phone && <Field label="Phone" value={detail.phone} />}
                  {detail.sponsor_name && <Field label="Sponsor / contact" value={detail.sponsor_name} />}
                  {detail.sponsor_email && <Field label="Sponsor email" value={detail.sponsor_email} />}
                  <Field label="Submitted" value={(detail.created_at || "").slice(0, 19).replace("T", " ")} />
                  <Field label="Email verified" value={detail.email_verified ? "Yes" : "No"} />
                  <Field label="Source" value={detail.source} />
                  <Field label="Notification" value={detail.notify_status || "—"} cap />
                  <div className="col-span-2"><Field label="Reason for request" value={detail.reason || "—"} /></div>
                </section>

                {/* Decision actions — placed first so they're always visible */}
                {!decided ? (
                  <section className="space-y-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Decision — choose one</p>
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
                      <p className="mb-2 flex items-center gap-1.5 text-sm font-bold text-emerald-800"><CheckCircle2 className="h-4 w-4" /> Approve</p>
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="text-slate-500">Requested: <b className="capitalize">{detail.requested_role}</b> · Assign role:</span>
                        <Select value={approveRole} onValueChange={setApproveRole}>
                          <SelectTrigger className="w-40" data-testid="reg-approve-role"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="student">Student</SelectItem>
                            <SelectItem value="faculty">Faculty</SelectItem>
                            <SelectItem value="advisor">Academic Advisor</SelectItem>
                            <SelectItem value="employer">Employer</SelectItem>
                          </SelectContent>
                        </Select>
                        <span className="text-slate-500">· Status:</span>
                        <Select value={approveStatus} onValueChange={setApproveStatus}>
                          <SelectTrigger className="w-36" data-testid="reg-approve-status"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button onClick={approve} disabled={busy} className="bg-[#5fa114] hover:bg-[#4f8a10]" data-testid="reg-approve-btn">
                          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (approveStatus === "active" ? "Approve & activate" : "Approve (inactive)")}
                        </Button>
                      </div>
                      <p className="mt-2 text-[11px] text-slate-400">{approveStatus === "active"
                        ? "Creates the account and emails a set-password link so they can sign in."
                        : "Creates the account but blocks sign-in until you activate it later (no email sent yet)."}</p>
                    </div>

                    <div className="rounded-xl border border-sky-200 bg-sky-50/40 p-4">
                      <p className="mb-2 flex items-center gap-1.5 text-sm font-bold text-sky-800"><HelpCircle className="h-4 w-4" /> Request more information</p>
                      <Textarea value={infoMsg} onChange={(e) => setInfoMsg(e.target.value)} rows={2} placeholder="What do you need from the applicant?" data-testid="reg-info-msg" />
                      <Button onClick={requestInfo} disabled={busy || !infoMsg.trim()} className="mt-2 bg-sky-600 hover:bg-sky-700" data-testid="reg-info-btn">Send request</Button>
                    </div>

                    <div className="rounded-xl border border-red-200 bg-red-50/40 p-4">
                      <p className="mb-2 flex items-center gap-1.5 text-sm font-bold text-red-700"><XCircle className="h-4 w-4" /> Reject</p>
                      <Input value={rejectInternal} onChange={(e) => setRejectInternal(e.target.value)} placeholder="Internal reason (required, never emailed)" className="mb-2" data-testid="reg-reject-internal" />
                      <Input value={rejectMsg} onChange={(e) => setRejectMsg(e.target.value)} placeholder="Optional message to applicant" data-testid="reg-reject-msg" />
                      <Button onClick={reject} disabled={busy || !rejectInternal.trim()} variant="destructive" className="mt-2" data-testid="reg-reject-btn">Reject request</Button>
                    </div>
                  </section>
                ) : (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm" data-testid="reg-decided">
                    <ShieldAlert className="mb-1 h-4 w-4 text-slate-400" />
                    Decision: <b className="capitalize">{detail.decision}</b> by {detail.decision_by} on {(detail.decision_at || "").slice(0, 16).replace("T", " ")}
                    {detail.approved_role && <> · Role: <b className="capitalize">{detail.approved_role}</b></>}
                    {detail.reject_internal_reason && <p className="mt-1 text-slate-500">Internal reason: {detail.reject_internal_reason}</p>}
                  </div>
                )}

                {/* Administrative review */}
                <section className="rounded-xl border border-slate-200 p-4">
                  <p className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-400">Administrative review</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-slate-500">Assigned to</span>
                    <Select value={detail.assigned_to || "none"} onValueChange={(v) => assign(v === "none" ? "" : v)}>
                      <SelectTrigger className="w-56" data-testid="reg-assign"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {admins.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {detail.notify_status === "failed" && (
                      <Button size="sm" variant="outline" onClick={resend} disabled={busy} data-testid="reg-resend">
                        <RefreshCw className="mr-1.5 h-4 w-4" /> Retry notification
                      </Button>
                    )}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…" data-testid="reg-note-input" />
                    <Button variant="outline" onClick={addNote} disabled={busy || !note.trim()} data-testid="reg-note-btn">Add</Button>
                  </div>
                  {detail.internal_notes?.length > 0 && (
                    <ul className="mt-3 space-y-1.5">
                      {detail.internal_notes.map((n, i) => (
                        <li key={i} className="rounded-lg bg-slate-50 px-3 py-1.5 text-xs text-slate-600">
                          <span className="font-semibold text-[#003057]">{n.author}</span> · {n.at?.slice(0, 16).replace("T", " ")}<br />{n.text}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                {/* Activity / audit trail */}
                <section className="rounded-xl border border-slate-200 p-4">
                  <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Activity history</p>
                  <ul className="space-y-1.5">
                    {(detail.activity || []).map((a, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-slate-500" data-testid={`reg-activity-${i}`}>
                        <span className="font-mono-num text-slate-400">{a.at?.slice(5, 16).replace("T", " ")}</span>
                        <span className="font-semibold text-[#003057]">{a.action}</span>
                        {a.note && <span className="text-slate-400">— {a.note}</span>}
                      </li>
                    ))}
                  </ul>
                </section>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}

function Field({ label, value, cap }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`text-[#003057] ${cap ? "capitalize" : ""}`}>{value}</p>
    </div>
  );
}
